import {
  createExtractionPrompt,
  deleteExtractionPrompt,
  searchExtractionPrompts,
  searchExtractionPromptVersions,
  updateExtractionPrompt,
  type ContractManagementColumnExtractionScope,
  type ExtractionPromptCreate,
  type ExtractionPromptInDb,
  type ExtractionPromptSearchResult,
  type ExtractionPromptUpdate,
  type ExtractionPromptVersionInDb,
} from '@/api';
import { extractErrorDetail } from '@/utils/errorHandler';

/**
 * Which of the organisation's prompts to search: those of any of the contract types, the
 * standalone ones, or (given both) either.
 */
export interface ExtractionPromptFilter {
  contractTypeUids?: string[];
  withoutContractType?: boolean;
}

/** A sort of the prompts: a prompt field (`display_name`, `created_at`, …) and a direction. */
export interface PromptSort {
  key: string;
  direction: 'asc' | 'desc';
}

export const ExtractionPromptsService = {
  /**
   * Search the organisation's extraction prompts: all of them, or those the filter picks.
   * @param viewUid Marks each result with the columns of that view whose active question it is.
   */
  async search({
    filter,
    text,
    offset,
    limit,
    viewUid,
    sortKey = 'display_name',
    sortDirection = 'asc',
  }: {
    /** Leave out for all prompts. */
    filter?: ExtractionPromptFilter;
    text: string;
    offset: number;
    /** The backend allows at most 50. */
    limit: number;
    viewUid?: string;
    /** A column of the prompt, e.g. `display_name`, `created_at`, `response_type`. */
    sortKey?: string;
    sortDirection?: 'asc' | 'desc';
  }): Promise<ExtractionPromptSearchResult> {
    try {
      const response = await searchExtractionPrompts({
        query: {
          ...(filter?.contractTypeUids?.length && { contract_type_uids: filter.contractTypeUids }),
          ...(filter?.withoutContractType && { without_contract_type: true }),
          ...(viewUid && { view_uid: viewUid }),
        },
        body: { text, offset, limit, sort_key: sortKey, sort_direction: sortDirection },
      });

      if (!response.data) {
        throw new Error('Failed to search extraction prompts');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorDetail(error, 'Failed to search extraction prompts. Please try again.'),
        { cause: error }
      );
    }
  },

  /** The questions the prompt has asked, newest version first (the backend allows at most 50). */
  async searchVersions(uid: string): Promise<ExtractionPromptVersionInDb[]> {
    try {
      const response = await searchExtractionPromptVersions({
        path: { uid },
        body: { text: '', offset: 0, limit: 50 },
      });
      if (!response.data) {
        throw new Error('Failed to load the prompt versions');
      }
      return response.data.results;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to load the prompt versions.'), {
        cause: error,
      });
    }
  },

  /** Org admins only. */
  async create(body: ExtractionPromptCreate): Promise<ExtractionPromptInDb> {
    try {
      const response = await createExtractionPrompt({ body });
      if (!response.data) {
        throw new Error('Failed to create extraction prompt');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to create the prompt. Please try again.'), {
        cause: error,
      });
    }
  },

  /**
   * Org admins only. A changed question re-extracts, with *scope*, every view column whose active
   * question the prompt is; name and contract type edits re-extract nothing.
   */
  async update(
    uid: string,
    body: ExtractionPromptUpdate,
    scope: ContractManagementColumnExtractionScope
  ): Promise<ExtractionPromptInDb> {
    try {
      const response = await updateExtractionPrompt({
        path: { uid },
        query: { extraction_scope: scope },
        body,
      });
      if (!response.data) {
        throw new Error('Failed to update extraction prompt');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to update the prompt. Please try again.'), {
        cause: error,
      });
    }
  },

  /** Org admins only. Refused (409) while a view column asks the prompt. */
  async remove(uid: string): Promise<void> {
    try {
      await deleteExtractionPrompt({ path: { uid } });
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to delete the prompt. Please try again.'), {
        cause: error,
      });
    }
  },
};
